import React, { useRef } from "react";
import { useTranslation } from "react-i18next";

import { Avatars } from "./Avatars";
import { ConfettiAnimation } from "./ConfettiAnimation";
import { CongratsActionButton } from "./Actions/CongratsActionButton";
import { CongratsActionLinkButton } from "./Actions/CongratsActionLinkButton";
import { CheckIcon } from "./CheckIcon";
import { CongratsCalendarActionButton } from "./Actions/CongratsCalendarActionButton";
import { AvatarLabel } from "./AvatarLabel";
import { cn } from "@/lib/utils";

interface CongratsProps {
  title: React.ReactNode;
  description: React.ReactNode;
  actions: React.ReactNode;
  icon: React.ReactNode;
  avatars: React.ReactNode;
  className: string;
}

export const Congrats = ({
  title,
  description,
  actions,
  icon,
  avatars,
  className,
}: Partial<CongratsProps>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  return (
    <section
      role="region"
      aria-label={t("congrats.ariaLabel.congrats")}
      ref={containerRef}
      className={cn(
        "relative h-full w-full overflow-hidden bg-gradient-to-b from-congrats-accent-primary via-congrats-accent-secondary to-background",
        className,
      )}
    >
      <ConfettiAnimation containerRef={containerRef} visibleFraction={0} />

      {/* Content layer */}
      <div
        className="relative z-10 mb-4 flex h-full w-full flex-col items-center justify-center gap-9"
        role="group"
        aria-label={t("congrats.ariaLabel.congratsContent")}
      >
        <div aria-hidden="true" tabIndex={-1}>
          {icon}
        </div>
        <h1 className="px-6 text-center text-2xl font-bold text-wrap text-foreground">
          {title}
        </h1>
        <section
          role="group"
          aria-label={t("congrats.ariaLabel.avatars")}
          className="flex flex-col items-center gap-5"
        >
          {avatars}
        </section>
        <span
          className="text-center text-base text-wrap text-foreground"
          role="group"
          aria-label={t("congrats.ariaLabel.description")}
        >
          {description}
        </span>
        <div
          className="flex flex-col gap-4"
          role="group"
          aria-label={t("congrats.ariaLabel.congratsActions")}
        >
          {actions}
        </div>
      </div>
    </section>
  );
};

Congrats.ActionButton = CongratsActionButton;
Congrats.ActionButtonLink = CongratsActionLinkButton;
Congrats.CheckIcon = CheckIcon;
Congrats.CalendarActionButton = CongratsCalendarActionButton;
Congrats.Avatars = Avatars;
Congrats.AvatarLabel = AvatarLabel;
